export function roundCurrencyValue(value, precision = 2) {
  const factor = 10 ** precision
  return Math.round(Number(value || 0) * factor) / factor
}

export function formatCurrencyNumber(value, {
  precision = 2,
  minimumFractionDigits = precision,
  maximumFractionDigits = precision,
} = {}) {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return ''

  return new Intl.NumberFormat('es-MX', {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(roundCurrencyValue(numericValue, precision))
}

export function sanitizeCurrencyInput(value, { allowNegative = false } = {}) {
  const stringValue = String(value ?? '')
    .replace(/[^\d,.-]/g, '')
    .replace(/(?!^)-/g, '')

  if (allowNegative) return stringValue
  return stringValue.replace(/-/g, '')
}

export function parseCurrencyInput(value, { precision = 2 } = {}) {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number') {
    return Number.isFinite(value) ? roundCurrencyValue(value, precision) : null
  }

  const sanitizedValue = sanitizeCurrencyInput(value, { allowNegative: true }).trim()
  if (!sanitizedValue) return null

  const negative = sanitizedValue.startsWith('-')
  const unsignedValue = sanitizedValue.replace(/-/g, '')
  const lastDot = unsignedValue.lastIndexOf('.')
  const lastComma = unsignedValue.lastIndexOf(',')
  const separatorIndex = Math.max(lastDot, lastComma)

  let integerPart = unsignedValue
  let decimalPart = ''

  if (separatorIndex >= 0) {
    integerPart = unsignedValue.slice(0, separatorIndex)
    decimalPart = unsignedValue.slice(separatorIndex + 1)
  }

  const normalizedInteger = integerPart.replace(/\D/g, '')
  const normalizedDecimal = decimalPart.replace(/\D/g, '').slice(0, precision)

  if (!normalizedInteger && !normalizedDecimal) return null

  const normalizedValue = `${negative ? '-' : ''}${normalizedInteger || '0'}${normalizedDecimal ? `.${normalizedDecimal}` : ''}`
  const parsed = Number(normalizedValue)

  return Number.isFinite(parsed) ? roundCurrencyValue(parsed, precision) : null
}

export function toCurrencyInputString(value, { precision = 2 } = {}) {
  const parsed = parseCurrencyInput(value, { precision })
  if (parsed === null) return ''

  return Number.isInteger(parsed)
    ? String(parsed)
    : parsed.toFixed(precision).replace(/0+$/, '').replace(/\.$/, '')
}
